<template>
  <div
    v-if="productHydrated"
    class="ProductCard relative h-full"
  >
    <div v-if="design === 'accessories'" class="flex justify-between mb-12 gap-12">
      <div class="basis-96 shrink-0 relative">
        <div
          v-if="showQuickbuy && !isPurchaseDisabledByFlag"
          class="absolute bottom-16 right-16"
        >
          <button
            v-if="canPurchaseProduct && (productHydrated.isD2C ? quickBuyActiveVariant.isInStock : quickBuyActiveVariant.canAddToCart)"
            class="bg-darkest h-32 w-32 text-center flex items-center justify-center hover:bg-darker transition-all"
            :title="$t('quickbuy.button')"
            type="button"
            @click="openQuickbuy"
          >
            <img
              src="/icons/cart-add-inv.svg"
              class="w-20 h-20"
              alt=""
            >
          </button>
        </div>

        <nuxt-img
          :src="currentImage"
          provider="norce"
          preset="plpimage"
          :sizes="sizes"
          class="w-full h-full object-cover opacity-100 group-hover:opacity-80 transition ease-out duration-500"
          :class="{
            'max-w-full max-h-full object-cover': productHydrated.extendedLayout,
          }"
          :alt="productHydrated?.name || ''"
          :loading="placement > 1 ? 'lazy':'eager'"
        />
      </div>
      <div class="basis-full">
        <nuxt-link
          :to="link"
          :class="{
            'pointer-events-none': userStore.isLoggedIn && pointShop
          }"
          @click="track"
        >
          <div class="type-headline-sm mb-2">{{ productHydrated.name }}</div>
          <div v-if="productHydrated.lastChildCategory?.name" class="type-sm mb-4">
            {{ productHydrated.lastChildCategory.name }}
          </div>

          <ProductPrice
            v-if="!globalContent.disablePricingAndPurchase && !globalContent.disablePricingB2C && !globalContent.hidePricesForSamplesOnly && !isPurchaseDisabledByFlag"
            :crossell="false"
            :product-item="productHydrated"
            :active-variant="quickBuyActiveVariant"
            :price-part-no-override="b2cPricePartNo"
            design="plpGrid"
          />

          <StockStatus
            v-if="!globalContent.disablePricingAndPurchase && !isPurchaseDisabledByFlag"
            :variant="quickBuyActiveVariant"
            :product-item="productHydrated"
            :use-short-labels="true"
            :is-product-card="true"
            :use-area="true"
            use-primary-stock-status
          />
        </nuxt-link>
      </div>
    </div>

    <!-- * * * * * * * Grid view -->
    <div
      v-else
      :class="{
        'flex bg-bg-subtle': featured,
      }"
      @mouseenter="imageHoverering = true"
      @mouseleave="imageHoverering = false"
    >

      <div
        class="relative aspect-product group overflow-hidden cursor-pointer"
        :class="{
          'hidden': design === 'hotSpot',
          'basis-1/3 desk:basis-1/2': featured,
        }"
      >

        <div
          v-if="!image"
        >
          <img
            src="/icons/no-image.svg"
            class="object-cover"
            alt=""
          >
        </div>
        <div
          v-else
          class="w-full h-full flex items-center justify-center relative"
        >
          <nuxt-img
            :src="currentImage"
            provider="norce"
            preset="plpimage"
            :sizes="sizes"
            class="w-full h-full object-cover opacity-100 group-hover:opacity-80 transition ease-out duration-500"
            :class="{
              'max-w-full max-h-full object-cover': productHydrated.extendedLayout,
            }"
            :alt="productHydrated?.name || ''"
            :loading="placement > 1 ? 'lazy':'eager'"
          />
        </div>

        <!-- Pointer-only duplicate of the title link below; hidden from AT to avoid a second, unnamed tab stop -->
        <nuxt-link
          :to="link"
          aria-hidden="true"
          tabindex="-1"
          :class="{
            'pointer-events-none': userStore.isLoggedIn && pointShop
          }"
          @click="track"
        >
          <div
            class="imageOverlay absolute top-0 left-0 w-full h-full"
          />
        </nuxt-link>

        <div
          class="absolute top-8 right-8 desk:top-12 desk:right-12 flex gap-8"
        >
          <button
            class="h-32 w-32  flex items-center justify-center transition-colors"
            :title="isInCompare(productHydrated.partNo) ? $t('compare.button.remove') : $t('compare.button.add')"
            :class="{
              'bg-bg-brand': isInCompare(productHydrated.partNo),
              'bg-bg-base': !isInCompare(productHydrated.partNo),
            }"
            @click.prevent="toggleCompare({ partNo: productHydrated.partNo, name: productHydrated.name, imageUrl: image ?? '', url: link })"
          >
            <img
              :src="isInCompare(productHydrated.partNo) ? '/icons/move-left-right-inv.svg':'/icons/move-left-right.svg'"
              class="w-20 h-20 shrink-0"
              alt=""
            >
          </button>
          <FavoriteButton
            v-if="userStore.canFavorizeProducts && !productHydrated.extendedLayout && !featured"
            :product="productHydrated"
            :design="design"
          />
        </div>

        <div
          v-if="showQuickbuy && !featured && !hideResilientProducts && !globalContent.disablePricingAndPurchase && !hideResilientStockStatus && !isPurchaseDisabledByFlag"
          class="absolute desk:bottom-12 desk:right-12 bottom-8 right-8"
        >
          <button
            v-if="canPurchaseProduct && (productHydrated.isD2C ? quickBuyActiveVariant.isInStock : quickBuyActiveVariant.canAddToCart)"
            class="bg-darkest h-32 w-32 text-center flex items-center justify-center hover:bg-darker transition-all"
            :title="$t('quickbuy.button')"
            type="button"
            @click="openQuickbuy"
          >
            <img
              src="/icons/cart-add-inv.svg"
              class="w-20 h-20"
              alt=""
            >
          </button>
          <!--          <InstantBuyBox
            v-else-if="showInstantBuyBox"
            :product-item="productHydrated"
            :active-variant="quickBuyActiveVariant"
          />-->
        </div>
        <div
          v-else-if="userStore.isLoggedIn && pointShop"
          class="absolute desk:bottom-12 bottom-8 left-0 w-full text-center"
        >
          <PointShopAmount :user-price="userPrice" :product="productHydrated" />
        </div>

        <!-- flags, top left -->
        <div class="type-tagline-xs normal-case leading-single absolute top-0 left-0">
          <!-- user price discount percent -->
          <div
            v-if="!globalContent.hidePricesForSamplesOnly && userPrice.price?.isCampaignPrice && userPrice.price?.discountPercentage && userPrice.price.discountPercentage > 0"
            class="inline-block text-center leading-single px-12 py-8 mb-4 bg-bg-errorStrong text-text-inverse"
          >
            {{ userPrice.price.discountPercentage }}%
          </div>

          <!-- campaign text from flags -->
          <div
            v-if="productHydrated.hasBulkDiscount"
            class="inline-block text-center leading-single px-4 py-4 text-dark"
          >
            {{ productHydrated.isGiftCampaign ? $t('buyMore.plp.gift-flag') : $t('buyMore.plp.flag') }}
          </div>

          <!-- * * * * display flags -->
          <div v-if="cardDisplayFlags.length">
            <div
              v-for="flag in cardDisplayFlags"
              :key="flag.code"
              class="inline-block text-center leading-single px-12 py-8 mb-4 bg-bg-errorStrong text-text-inverse"
            >
              {{ $squareMeterFormatting(flag.text) }}
            </div>
          </div>

          <div
            v-for="flag in productHydrated.productCardDisplayFlags"
            :key="flag.code"
          >
            <div
              v-if="shouldShowFlag(flag.code)"
              :style="`background-color: ${flag.background}`"
              class="inline-block text-center leading-single px-12 py-8 mb-4 text-text-inverse bg-bg-inverse"
              :class="{
                'text-lightest h-24 px-8 py-[7px] relative desk:top-4 desk:left-4': isDarkFlag(flag.background),
                'type-tiny': flag.code === 'colorMap',
              }"
            >
              {{ $squareMeterFormatting(flag.text) }}
            </div>
          </div>
          <!-- * * * * hidden product -->
          <div v-if="productHydrated.isHiddenProduct" class="leading-single px-4 py-4 text-dark">
            <img
              alt=""
              src="/icons/eye-closed.svg"
              class="w-[16px] h-[16px]"
            >
          </div>
        </div>
        <!-- more variants - bottom left -->
        <div
          v-if="productHydrated.hasVariants && !pointShop && !featured"
          class="absolute left-0 bottom-8 desk:bottom-12 flex items-center min-h-24 desk:min-h-32 py-2 px-8 desk:px-12
                 type-tagline leading-single
                 bg-bg-successStrong text-text-inverse"
          :class="{
            nextToQuickbuy: showQuickbuy,
            'hidden': design === 'miniCart',
          }"
        >
          {{ $t('productList.product.hasVariants') }}
        </div>
      </div>
      <div
        :class="{
          'w-full': !featured,
          'basis-2/3 desk:basis-1/2 p-16 desk:p-24 flex flex-col justify-center': featured,
          'px-0 desk:px-0 pt-0 desk:pt-0 pb-0': design === 'hotSpot',
          'px-8 pt-12 desk:px-4 desk:pt-12 pb-32': design === 'standard',
        }"
      >
        <nuxt-link
          :to="link"
          :class="{
            'pointer-events-none': userStore.isLoggedIn && pointShop
          }"
          @click="track"
        >
          <div v-if="featured" class="type-tagline text-text-subtle mb-4">{{ productHydrated.lastChildCategory?.name }}</div>
          <div v-if="miniCart" class="type-tagline-sm text-text-subtle">{{ productHydrated.partNo }}</div>
          <div
            class="type-headline-sm mb-2"
            :class="{
              'mt-4': design === 'small'
            }"
          >
            {{ productHydrated.name }}
          </div>

          <div
            v-if="globalContent.getCountryCode === 'de' && isFloor && !miniCart && !featured"
            class="type-sm mb-4"
          >
            {{ productHydrated.parametricByCode('ProductConstruction')?.listValue?.name }}
          </div>

          <div v-else-if="productHydrated.lastChildCategory?.name && !miniCart && !featured" class="type-sm mb-4">
            {{ productHydrated.lastChildCategory.name }}
          </div>

          <div
            v-if="showShortDescription"
            class="type-sm text-text-subtle mb-24"
            v-html="productHydrated.shortDescription"
          />

          <div
            v-if="productHydrated.extendedLayout"
            class="leading-single type-xs-medium desk:type-sm-medium"
          >
            <div v-if="productHydrated.hasVariants">
              {{ $t('productPage.extended.shades', { num: productHydrated.partNumbers.length }) }}
            </div>
          </div>
          <div v-else-if="!hideResilientProducts && !globalContent.disablePricingAndPurchase && !globalContent.disablePricingB2C && !globalContent.hidePricesForSamplesOnly && !isPurchaseDisabledByFlag">
            <div class="type-base mt-8">
              <div
                v-if="userPrice.status === 'logginIn' || userPrice.status === 'pending'"
                class="priceLoadBox"
              >
                {{ productPrice(productHydrated.priceInfo, 'price', true)?.replace(/\d/g, '&nbsp;') }}
              </div>
              <div
                v-if="userPrice.status === 'notLoggedIn' && !globalContent.getShowPriceNonLoggedInB2B && !globalContent.getIsGlobalMarket"
                class="btn--text whitespace-normal"
                @click.prevent="showLogin"
              >
                {{ $t('price.notLoggedIn') }}
              </div>

              <div
                v-if="userPrice.status === 'error' && globalContent.getIsB2b"
                class="text-criticalDark"
              >
                ---
              </div>

              <ProductPrice
                :crossell="false"
                :product-item="productHydrated"
                :active-variant="quickBuyActiveVariant"
                :price-part-no-override="b2cPricePartNo"
                design="plpGrid"
              />
            </div>
          </div>
        </nuxt-link>
        <div
          v-if="shouldShowStockStatus && userPrice.status !== 'notLoggedIn' && !globalContent.getIsB2c && !hideResilientStockStatus && !globalContent.disablePricingAndPurchase && !isPurchaseDisabledByFlag"
          class="flex items-center px-4 py-2 mt-6 desk:mt-8"
        >
          <StockStatus
            :variant="quickBuyActiveVariant"
            :product-item="productHydrated"
            :use-short-labels="true"
            :is-product-card="true"
            :use-area="true"
            use-primary-stock-status
          />
        </div>
        <div
          v-if="globalContent.getIsB2c && productHydrated.hasB2CSample && !globalContent.getIsGlobalMarket"
          class="flex items-center gap-6 px-4 py-2 mt-6 desk:mt-8 leading-single type-sm"
        >
          <div class="w-8 h-8 inline-block bg-bg-successStrong rounded-full"/>
          {{ $t('samples.b2c.available') }}
        </div>
        <ProductCardStoreStatus
          v-if="globalContent.getNewB2CDesign && !globalContent.getIsGlobalMarket"
          :product="productHydrated"
        />
      </div>
      <div v-if="featured && userStore.isLoggedIn" class="absolute top-8 right-8 desk:top-12 desk:right-12">
        <FavoriteButton
          :product="productHydrated"
          :design="design"
        />
      </div>
      <div
        v-if="showQuickbuy && featured && !hideResilientStockStatus && !isPurchaseDisabledByFlag"
        class="absolute desk:bottom-12 desk:right-12 bottom-8 right-8"
      >
        <button
          v-if="canPurchaseProduct && quickBuyActiveVariant.canAddToCart"
          class="bg-darkest h-32 w-32 text-center flex items-center justify-center hover:bg-darker transition-all"
          :title="$t('quickbuy.button')"
          type="button"
          @click="openQuickbuy"
        >
          <img
            src="/icons/cart-add-inv.svg"
            class="w-20 h-20"
            alt=""
          >
        </button>
        <InstantBuyBox
          v-else-if="showInstantBuyBox"
          :product-item="productHydrated"
          :active-variant="quickBuyActiveVariant"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import FavoriteButton from '~/components/product/FavoriteButton.vue';
import useCompare from '~/composables/useCompare';
import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';
import type { ProductRaw } from '#constants/types/algolia';
import type { ProductModel } from '#models/product';
import Product from '#models/product';
import { useCartStore } from '~/store/cart';
import { useGlobalContentStore } from '~/store/globalContent';
import { useUserStore } from '~/store/user';
import { isLight } from '~/util/colorUtil';
import PointShopAmount from '~/components/product/PointShopAmount.vue';
import { createDataLayerItem, sendDataLayer } from '~/composables/useTracking';
import useLinkReplacer from '~/composables/useLinkReplacer';
import { ref } from 'vue';
import type { ProductVariantModel } from '#models/productVariant';
import InstantBuyBox from '~/components/product/InstantBuyBox.vue';
import ProductPrice from '~/components/product-page/ProductPrice.vue';
import StockStatus from '~/components/product/StockStatus.vue';
import ProductCardStoreStatus from '~/components/product/ProductCardStoreStatus.vue';
import usePrices from '~/composables/usePrices';
import useAlgoliaInsights from '~/composables/useAlgoliaInsights';
import useProductPurchase from '~/composables/useProductPurchase';

const cartStore = useCartStore();
const { canPurchase } = useProductPurchase();
const userStore = useUserStore();
const globalContent = useGlobalContentStore();
const uiStore = useUiStore();
const { listImageSize } = storeToRefs(uiStore);
const { productPrice } = usePrices();

const { productUrl } = useLinkReplacer();
const { isInCompare, toggleCompare } = useCompare();
const props = withDefaults(defineProps<{
  product: ProductRaw | ProductModel,
  enableQuickBuy: boolean,
  placement: number, // the placement of list component
  index?: number, // the placement inside the list
  design: 'standard' | 'featured' | 'small' | 'hotSpot' | 'multiQuickbuy' | 'accessories' | 'miniCart',
  pointShop?: boolean,
  isUpsell?: boolean,
  useVariantImage?: boolean, // if the list from algolia is distinct = true, we show the variant image
  listIndex?: string,
  showStockStatus?: boolean,
}>(), {
  showStockStatus: undefined,
});

defineEmits(['quickbuy']);

const productHydrated = props.product instanceof Product ? props.product : Product.create(props.product);
if (!(props.product instanceof Product)) {
  console.warn(`Using ProductCard from ProductRaw is not recommended: ${productHydrated.partNo}`);
}

const setPrimaryToActive = () => {
  if (productHydrated) {
    let primaryVariant = props.listIndex?.includes('price')
      ? productHydrated.variants.findIndex((f) => f.partNo === props.product.partNo)
      : productHydrated.variants.findIndex((f) => f.isPrimary);
    if (primaryVariant === -1) {
      primaryVariant = 0;
    }
    quickBuyActiveVariant.value = productHydrated.variants[primaryVariant];
  }
};
const quickBuyActiveVariant = ref<ProductVariantModel>(productHydrated.variants[0]);

setPrimaryToActive();

const cardDisplayFlags = computed(() => {
  return productHydrated.displayFlagsForVariant(quickBuyActiveVariant.value);
});

const imageHoverering = ref(false);
const image = computed(() => {
  if (props.useVariantImage && productHydrated.variantImage) {
    return productHydrated.variantImage;
  }
  // check if we have a "swatch image"
  const swatchImage = productHydrated.mediaSets.find(f => f.code === 'PrimaryImage');
  if (swatchImage && swatchImage.media[0]) {
    return swatchImage.media[0].url;
  }

  return productHydrated.listImage;
});
const hoverImage = computed(()=> {
  const hoverImage = productHydrated.mediaSets.find(f => f.code === 'InteriorImage');
  if (hoverImage && hoverImage.media[0]) {
    return hoverImage.media[0].url;
  }
  return null;
});

const currentImage = computed(()=> {
  if (imageHoverering.value && hoverImage.value) {
    return hoverImage.value;
  }
  return image.value;
});

const featured = computed(()=> {
  return props.design === 'featured';
});

const miniCart = computed(()=> {
  return props.design === 'miniCart';
});

const showShortDescription = computed(()=> {
  return productHydrated.shortDescription && (featured.value || miniCart.value);
});

const link = computed<string>(()=> {
  if (globalContent.getIsB2c && productHydrated.primaryUrl) {
    return productUrl(productHydrated.primaryUrl);
  }
  return productUrl(productHydrated.url);
});

const b2cPricePartNo = computed<string | undefined>(() => {
  if (globalContent.getIsB2c) {
    return productHydrated.priceFetchPartNo || undefined;
  }
  return undefined;
});

const userPrice = computed(()=> {
  return props.pointShop || props.listIndex?.includes('price') ?
    userStore.getPrice(productHydrated.partNo, props.isUpsell) :
    userStore.getPriceWithFallback(productHydrated.priceFetchPartNo, productHydrated.partNo, props.isUpsell);
});

const openQuickbuy = async() => {
  cartStore.setCurrentlyBuying(productHydrated, props.isUpsell, !props.listIndex?.includes('price'));
  uiStore.setShowQuickBuy(true);
  uiStore.setShowMiniCart(false);
};

const { trackProductClick } = useAlgoliaInsights();

const track = (event?: Event) => {
  sendDataLayer(
    'select_item',
    [ createDataLayerItem(props.product) ],
    {
      'item_list_name': globalContent.currentStory?.name,
      'item_list_id': globalContent.currentStory?.id,
    }
  );

  if (event?.target) {
    const target = event.target as HTMLElement;
    const parent = target.closest('.algoliaClickTrigger');

    if (parent) {
      const objectId = parent.getAttribute('data-insights-object-id');
      const position = parent.getAttribute('data-insights-position');
      const queryId = parent.getAttribute('data-insights-query-id');

      if (objectId && position) {
        trackProductClick(
          objectId,
          parseInt(position, 10),
          queryId || undefined
        );
      }
    }
  }
};

const sizes = computed(()=> {
  switch (listImageSize.value) {
    case 'lg':
      return 'mob375:100vw mob390:100vw mob414:100vw desk:538px';
    case 'sm':
      return 'mob375:368px mob390:382px mob414:406px desk:538px';
  }
  return 'mob375:368px mob390:382px mob414:406px desk:538px';
});

const showLogin = () => {
  uiStore.setShowSigninModal(true, true);
};

const isDarkFlag = (input: string | null | undefined) => {
  if (!input) {
    return false;
  }
  return !isLight(input);
};

const showQuickbuy = computed(()=> {
  return props.enableQuickBuy && (userStore.isLoggedIn || (productHydrated.isD2C && globalContent.getNorceCheckoutEnabled)) && !productHydrated.extendedLayout && !props.pointShop && !userStore.isSamplesOnlyUser && !userStore.isMediabankUser;
});

/**
 * Check if user can purchase this product based on pricelists
 * For D2C products, always allow (B2C doesn't use pricelist restrictions)
 * For B2B logged-in users, check if product is on their pricelists
 */
const canPurchaseProduct = computed(() => {
  // Check for disablePurchase control flag on the active variant
  if (quickBuyActiveVariant.value.isPurchaseDisabled) {
    return false;
  }
  if (userStore.isMediabankUser) {
    return false;
  }
  // D2C products bypass pricelist check (B2C checkout)
  if (productHydrated.isD2C) {
    return true;
  }
  // For B2B users, check pricelist eligibility
  return canPurchase(productHydrated);
});

const shouldShowFlag = (code: string) => {
  if (code === 'onDemand') {
    const anyInStock = props.product.variantStockStatuses.some((s) => s.inStock > 0);
    return !anyInStock;
  }
  return true;
};

// if it's floor or accessories
const isFloor = computed(()=> {
  return productHydrated.categories.some((s) => {
    return s.code === 'Wood' || s.code === 'Vinyl';
  });
});

/**
 * Hides price/purchase buttons for resilient products (US/DE)
 */
const hideResilientProducts = computed(()=> {
  const isResilient = productHydrated.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
  return isResilient && globalContent.disableResilientPricingAndPurchase;
});

/**
 * Hides price/purchase buttons when disablePurchase control flag is set
 */
const isPurchaseDisabledByFlag = computed(() => {
  return quickBuyActiveVariant.value.isPurchaseDisabled;
});

// Hides stock status for resilient products (shows for logged in users in US)
const hideResilientStockStatus = computed(()=> {
  const isResilient = productHydrated.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
  return isResilient && globalContent.disableResilientStockStatus;
});

const shouldShowStockStatus = computed(() => {
  if (props.showStockStatus !== undefined) {
    return props.showStockStatus;
  }
  // Hide StockStatus for 'small' design by if props is false
  return props.design !== 'small';
});

const showInstantBuyBox = computed(() => globalContent.getCountryCode !== 'fr');

</script>

<style scoped lang="postcss">
.priceLoadBox {
  @apply inline-block;
  color: rgba(0,0,0,0);
  animation-name: spin;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-direction: alternate;
}

@keyframes spin {
  from {
    background-color: #DEDDD9;
  }
  to {
    background-color: #F5F4F0;
  }
}

.nextToQuickbuy {
  /* 100% - padding - button size - margin to button */
  max-width: calc(100% - 16px - 32px - 4px);
}

</style>
